/**
 * CMS API integration for CRE8IT
 * Henter data fra de intuitive danske CMS-samlinger (sider, gallerier, ydelser, virksomhedsoplysninger)
 */

const API_BASE_URL = 'https://api.dev.auxo.dk';

/**
 * Normaliserer en sides felter så både danske og tidligere feltnavne er tilgængelige
 */
function normalizeSide(item) {
  if (!item) return null;
  const side_slug = item.side_slug || item.page_slug || '';
  const titel = item.titel || item.title || '';
  const overskrift = item.overskrift || item.headline || '';
  const underoverskrift = item.underoverskrift || item.subtitle || '';
  const meta_titel = item.meta_titel || item.meta_title || '';
  const meta_beskrivelse = item.meta_beskrivelse || item.meta_description || '';
  const indhold = item.indhold || item.content || '';
  const specifikationer = item.specifikationer || item.specifications || '';
  const priser = item.priser || item.prices || '';
  const billeder = item.billeder || item.images || [];
  const hero_billede = item.hero_billede || item.hero_image || (Array.isArray(billeder) && billeder.length > 0 ? [billeder[0]] : []);
  const hero_billede_alt = item.hero_billede_alt || item.hero_image_alt || '';

  const mellem_billede = item.mellem_billede || item.sekundaert_billede || item.secondary_image || (Array.isArray(billeder) && billeder.length > 1 ? [billeder[1]] : []);
  const mellem_billede_alt = item.mellem_billede_alt || item.sekundaert_billede_alt || item.secondary_image_alt || '';

  const om_eyebrow = item.om_eyebrow || '';
  const om_titel = item.om_titel || '';
  const om_tekst = item.om_tekst || '';
  const om_billede = item.om_billede || [];
  const om_billede_alt = item.om_billede_alt || '';
  const om_knap_tekst = item.om_knap_tekst || '';
  const om_knap_link = item.om_knap_link || '';

  const vaerksted_eyebrow = item.vaerksted_eyebrow || '';
  const vaerksted_titel = item.vaerksted_titel || '';
  const vaerksted_tekst = item.vaerksted_tekst || '';

  const hero_usp_1 = item.hero_usp_1 || '';
  const hero_usp_2 = item.hero_usp_2 || '';
  const hero_usp_3 = item.hero_usp_3 || '';

  return {
    ...item,
    side_slug,
    page_slug: side_slug,
    titel,
    title: titel,
    overskrift,
    headline: overskrift,
    underoverskrift,
    subtitle: underoverskrift,
    hero_billede,
    hero_image: hero_billede,
    hero_billede_alt,
    hero_image_alt: hero_billede_alt,
    mellem_billede,
    sekundaert_billede: mellem_billede,
    secondary_image: mellem_billede,
    mellem_billede_alt,
    sekundaert_billede_alt: mellem_billede_alt,
    secondary_image_alt: mellem_billede_alt,
    hero_usp_1,
    hero_usp_2,
    hero_usp_3,
    om_eyebrow,
    om_titel,
    om_tekst,
    om_billede,
    om_billede_alt,
    om_knap_tekst,
    om_knap_link,
    vaerksted_eyebrow,
    vaerksted_titel,
    vaerksted_tekst,
    meta_titel,
    meta_title: meta_titel,
    meta_beskrivelse,
    meta_description: meta_beskrivelse,
    indhold,
    content: indhold,
    specifikationer,
    specifications: specifikationer,
    priser,
    prices: priser,
    billeder,
    images: billeder,
  };
}

/**
 * Normaliserer et galleris felter
 */
function normalizeGalleri(item) {
  if (!item) return null;
  const side_slug = item.side_slug || item.page_slug || '';
  const titel = item.titel || item.title || '';
  const beskrivelse = item.beskrivelse || item.description || '';
  const cover_billede = item.cover_billede || item.cover_image || [];
  const billeder = item.billeder || item.images || [];

  return {
    ...item,
    side_slug,
    page_slug: side_slug,
    titel,
    title: titel,
    beskrivelse,
    description: beskrivelse,
    cover_billede,
    cover_image: cover_billede,
    billeder,
    images: billeder,
    badge_tekst: item.badge_tekst || '',
    badge_text: item.badge_tekst || '',
    billedtekster: item.billedtekster || '',
  };
}

/**
 * Parser feltet 'billedtekster' fra CMS til en liste af billedtekster.
 * Understøtter:
 * 1. Simpel linje for linje (linje 1 = billede 1, linje 2 = billede 2 osv.)
 * 2. Nummereret format (f.eks. "1: Min tekst" eller "1. Min tekst")
 * 3. Titel og beskrivelse adskilt af '|' eller ' - ' (f.eks. "Mercedes Vito | Fuld indpakning med logo")
 */
export function parseImageCaptions(billedteksterRaw, totalImages = 0) {
  if (!billedteksterRaw || typeof billedteksterRaw !== 'string') {
    return [];
  }

  const lines = billedteksterRaw
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  if (lines.length === 0) return [];

  // Tjek om brugeren anvender nummerering (f.eks. "1:", "1.", "#1", "1)")
  const numberedMap = {};
  let hasNumbered = false;

  lines.forEach((line) => {
    const match = line.match(/^#?(\d+)[\.\:\-\)]\s*(.+)$/);
    if (match) {
      hasNumbered = true;
      const index = parseInt(match[1], 10) - 1; // 1-indekseret til 0-indekseret
      numberedMap[index] = match[2].trim();
    }
  });

  const count = Math.max(totalImages, lines.length);
  const result = [];

  for (let i = 0; i < count; i++) {
    let rawText = '';
    if (hasNumbered) {
      rawText = numberedMap[i] || '';
    } else if (i < lines.length) {
      rawText = lines[i] || '';
    }

    if (rawText) {
      let title = rawText;
      let description = '';

      if (rawText.includes('|')) {
        const parts = rawText.split('|');
        title = parts[0].trim();
        description = parts.slice(1).join('|').trim();
      } else if (rawText.includes(' - ')) {
        const parts = rawText.split(' - ');
        title = parts[0].trim();
        description = parts.slice(1).join(' - ').trim();
      }

      result[i] = {
        title,
        description,
        fullText: rawText
      };
    } else {
      result[i] = null;
    }
  }

  return result;
}

/**
 * Henter alle sider fra CMS'et (sider)
 */
export async function fetchSider() {
  try {
    const response = await fetch(`${API_BASE_URL}/public/content/sider`);
    if (response.ok) {
      const data = await response.json();
      if (Array.isArray(data) && data.length > 0) {
        return data.map(normalizeSide);
      }
    }
  } catch (error) {
    console.warn('Kunne ikke hente fra sider samling, forsøger fallback:', error);
  }

  // Fallback til pages hvis sider endnu ikke er klar
  try {
    const fallbackRes = await fetch(`${API_BASE_URL}/public/content/pages`);
    if (fallbackRes.ok) {
      const data = await fallbackRes.json();
      return Array.isArray(data) ? data.map(normalizeSide) : [];
    }
  } catch (err) {
    console.error('Fejl ved hentning af sider fra CMS:', err);
  }
  return [];
}

/**
 * Alias for bagudkompatibilitet
 */
export const fetchPages = fetchSider;

/**
 * Henter en enkelt side baseret på side_slug / page_slug
 */
export async function fetchSideBySlug(slug) {
  try {
    const sider = await fetchSider();
    return sider.find((s) => s.side_slug === slug || s.page_slug === slug) || null;
  } catch (error) {
    console.error(`Fejl ved hentning af side med slug ${slug}:`, error);
    return null;
  }
}

/**
 * Alias for bagudkompatibilitet
 */
export const fetchPageBySlug = fetchSideBySlug;

/**
 * Henter alle gallerier fra CMS'et (gallerier)
 */
export async function fetchGallerier() {
  try {
    const response = await fetch(`${API_BASE_URL}/public/content/gallerier`);
    if (response.ok) {
      const data = await response.json();
      if (Array.isArray(data) && data.length > 0) {
        return data.map(normalizeGalleri);
      }
    }
  } catch (error) {
    console.warn('Kunne ikke hente fra gallerier samling, forsøger fallback:', error);
  }

  // Fallback til galleries
  try {
    const fallbackRes = await fetch(`${API_BASE_URL}/public/content/galleries`);
    if (fallbackRes.ok) {
      const data = await fallbackRes.json();
      return Array.isArray(data) ? data.map(normalizeGalleri) : [];
    }
  } catch (err) {
    console.error('Fejl ved hentning af gallerier fra CMS:', err);
  }
  return [];
}

/**
 * Normaliserer et galleri-billede fra den nye intuitive samling 'galleri_billeder' eller dedikerede gallerisamlinger
 */
function normalizeGalleriBillede(item, fallbackGallerySlug = '') {
  if (!item) return null;
  const id = item.id || item.slug?._ || String(Math.random());
  const titel = item.titel || item.title || '';
  const beskrivelse = item.beskrivelse || item.description || '';
  const imgArr = item.billede || item.image || [];
  
  // Saml alle gyldige billed-URLs og fjern dubletter
  let images = [];
  if (Array.isArray(imgArr)) {
    images = imgArr.filter(Boolean);
  } else if (typeof imgArr === 'string' && imgArr.trim()) {
    images = [imgArr.trim()];
  }
  // Fjern eventuelle eksakte dubletter i billed-listen
  images = [...new Set(images)];

  const url = images[0] || '';
  const galleri_slug = item.galleri_slug || item.gallery_slug || item.side_slug || fallbackGallerySlug;
  const galleri_titel = item.galleri_titel || '';
  const sortering = typeof item.sortering === 'number' ? item.sortering : 99;
  const terms = item.terms || {};
  const kategori = item.kategori || '';

  return {
    id,
    titel,
    title: titel,
    beskrivelse,
    description: beskrivelse,
    url,
    image: url,
    images, // Alle billeder tilknyttet albummet
    imageCount: images.length,
    isAlbum: images.length > 1,
    galleri_slug,
    gallery_slug: galleri_slug,
    galleri_titel,
    kategori,
    sortering,
    terms,
  };
}

/**
 * Mapping fra URL/side-slug til de dedikerede CMS-gallerisamlinger
 */
export const DEDICATED_GALLERY_COLLECTIONS = {
  'dekoration-bil-varebiler': 'galleri_dekoration_varebiler',
  'dekoration-bil-privat': 'galleri_dekoration_privat',
  'dekoration-lastbil': 'galleri_dekoration_lastbiler',
  'dekoration-vaeg': 'galleri_dekoration_vaeg',
  'solfilm-bil': 'galleri_solfilm_biler',
  'solfilm-maskin': 'galleri_solfilm_maskiner',
  'solfilm-hus': 'galleri_solfilm_hus',
  'interioerfolie': 'galleri_interioer_koekken',
  'interioerfolie-koekken': 'galleri_interioer_koekken',
  'interioerfolie-borde-moebler': 'galleri_interioer_borde_moebler',
  'interioerfolie-erhverv': 'galleri_interioer_erhverv',
  'oevrige': 'galleri_interioer_oevrige',
  'interioerfolie-oevrige': 'galleri_interioer_oevrige',
  'logodesign': 'galleri_logodesign',
};

/**
 * Henter billeder fra en specifik dedikeret CMS-gallerisamling
 */
export async function fetchDedicatedGallery(collectionName, fallbackSlug = '') {
  try {
    const response = await fetch(`${API_BASE_URL}/public/content/${collectionName}`);
    if (response.ok) {
      const data = await response.json();
      const items = Array.isArray(data) ? data : (data.items || []);
      if (items.length > 0) {
        const normalized = items
          .map((item) => normalizeGalleriBillede(item, fallbackSlug))
          .filter((b) => b && b.url);
        normalized.sort((a, b) => a.sortering - b.sortering);
        return normalized;
      }
    }
  } catch (err) {
    console.warn(`Kunne ikke hente fra dedikeret gallerisamling ${collectionName}:`, err);
  }
  return [];
}

export const ALL_DEDICATED_COLLECTIONS = [
  { name: 'galleri_dekoration_varebiler', slug: 'dekoration-bil-varebiler' },
  { name: 'galleri_dekoration_privat', slug: 'dekoration-bil-privat' },
  { name: 'galleri_dekoration_lastbiler', slug: 'dekoration-lastbil' },
  { name: 'galleri_dekoration_vaeg', slug: 'dekoration-vaeg' },
  { name: 'galleri_solfilm_biler', slug: 'solfilm-bil' },
  { name: 'galleri_solfilm_maskiner', slug: 'solfilm-maskin' },
  { name: 'galleri_solfilm_hus', slug: 'solfilm-hus' },
  { name: 'galleri_interioer_koekken', slug: 'interioerfolie-koekken' },
  { name: 'galleri_interioer_borde_moebler', slug: 'interioerfolie-borde-moebler' },
  { name: 'galleri_interioer_erhverv', slug: 'interioerfolie-erhverv' },
  { name: 'galleri_interioer_oevrige', slug: 'oevrige' },
  { name: 'galleri_logodesign', slug: 'logodesign' },
];

/**
 * Henter alle individuelle galleri-billeder for en slug eller samlet for alle gallerier.
 * Henter direkte fra de 9 dedikerede selvstændige CMS-gallerisamlinger.
 */
export async function fetchGalleriBilleder(galleriSlug) {
  // 1. Hvis en specifik slug er angivet
  if (galleriSlug) {
    if (DEDICATED_GALLERY_COLLECTIONS[galleriSlug]) {
      const collectionName = DEDICATED_GALLERY_COLLECTIONS[galleriSlug];
      const dedicatedItems = await fetchDedicatedGallery(collectionName, galleriSlug);
      if (dedicatedItems.length > 0) {
        return dedicatedItems;
      }
    }
  } else {
    // 2. Hvis ingen slug er angivet, hent fra alle dedikerede samlinger samlet
    try {
      const allResults = await Promise.all(
        ALL_DEDICATED_COLLECTIONS.map((c) => fetchDedicatedGallery(c.name, c.slug))
      );
      const flattened = allResults.flat();
      if (flattened.length > 0) {
        return flattened;
      }
    } catch (err) {
      console.warn('Kunne ikke hente samlede dedikerede gallerier:', err);
    }
  }

  // 3. Hvis ingen fundet
  return [];
}

/**
 * Alias for bagudkompatibilitet
 */
export const fetchGalleries = fetchGallerier;

/**
 * Henter et enkelt galleri baseret på side_slug / page_slug
 */
export async function fetchGalleriBySlug(slug) {
  try {
    const gallerier = await fetchGallerier();
    return gallerier.find((g) => g.side_slug === slug || g.page_slug === slug) || null;
  } catch (error) {
    console.error(`Fejl ved hentning af galleri med slug ${slug}:`, error);
    return null;
  }
}

/**
 * Alias for bagudkompatibilitet
 */
export const fetchGalleryBySlug = fetchGalleriBySlug;

/**
 * Henter alle ydelser fra CMS'et (ydelser)
 */
export async function fetchYdelser() {
  try {
    const response = await fetch(`${API_BASE_URL}/public/content/ydelser`);
    if (response.ok) {
      const data = await response.json();
      if (Array.isArray(data) && data.length > 0) {
        return data.sort((a, b) => (a.sortering || 0) - (b.sortering || 0));
      }
    }
  } catch (error) {
    console.error('Fejl ved hentning af ydelser fra CMS:', error);
  }
  return [];
}

/**
 * Henter virksomhedsoplysninger fra CMS'et (virksomhedsoplysninger)
 */
export async function fetchVirksomhedsoplysninger() {
  try {
    const response = await fetch(`${API_BASE_URL}/public/content/virksomhedsoplysninger`);
    if (response.ok) {
      const data = await response.json();
      if (Array.isArray(data) && data.length > 0) {
        return data[0];
      }
    }
  } catch (error) {
    console.error('Fejl ved hentning af virksomhedsoplysninger fra CMS:', error);
  }
  return null;
}

/**
 * Normaliserer en før/efter case fra CMS (foer_efter_cases)
 */
function normalizeFoerEfterCase(item) {
  if (!item) return null;
  const id = item.id || item.slug?._ || String(Math.random());
  const title = item.titel || item.title || '';
  const location = item.sted || item.location || '';
  const tag = item.kategori_tag || item.tag || '';
  const description = item.beskrivelse || item.description || '';
  const side_slug = item.side_slug || 'interioerfolie';
  const sortering = typeof item.sortering === 'number' ? item.sortering : 99;

  // Billeder (kan være array eller string)
  const beforeImgArr = item.billede_foer || item.beforeImage || [];
  const afterImgArr = item.billede_efter || item.afterImage || [];
  const beforeImage = Array.isArray(beforeImgArr) ? beforeImgArr[0] || '' : (beforeImgArr || '');
  const afterImage = Array.isArray(afterImgArr) ? afterImgArr[0] || '' : (afterImgArr || '');

  const beforeAlt = item.alt_foer || item.beforeAlt || `${title} før folie`;
  const afterAlt = item.alt_efter || item.afterAlt || `${title} efter folie`;

  // Fordele (kan være raw HTML string med <li> tags eller array af strenge)
  let benefits = [];
  if (Array.isArray(item.fordele)) {
    benefits = item.fordele;
  } else if (typeof item.fordele === 'string' && item.fordele) {
    const matches = item.fordele.match(/<li[^>]*>(.*?)<\/li>/gi);
    if (matches && matches.length > 0) {
      benefits = matches
        .map((m) => {
          // Fjern <li>...</li> og eventuelle indre HTML-tags som <span>, <p>, <strong> mv.
          const clean = m.replace(/<\/?li[^>]*>/gi, '').replace(/<[^>]+>/g, '').trim();
          return clean;
        })
        .filter(Boolean);
    } else {
      // Del på linjeskift hvis almindelig tekst eller tags
      const stripped = item.fordele.replace(/<[^>]+>/g, '\n');
      benefits = stripped
        .split('\n')
        .map((s) => s.replace(/^[•\-\*]\s*/, '').trim())
        .filter(Boolean);
    }
  }

  return {
    id,
    title,
    titel: title,
    location,
    sted: location,
    tag,
    kategori_tag: tag,
    description,
    beskrivelse: description,
    benefits,
    fordele_raw: item.fordele || '',
    beforeImage,
    afterImage,
    billede_foer: beforeImage,
    billede_efter: afterImage,
    beforeAlt,
    afterAlt,
    alt_foer: beforeAlt,
    alt_efter: afterAlt,
    side_slug,
    sortering,
  };
}

/**
 * Henter før/efter cases fra CMS'et (foer_efter_cases)
 */
export async function fetchFoerEfterCases(sideSlug) {
  try {
    const url = `${API_BASE_URL}/public/v2/content/foer_efter_cases?sort=sortering&order=asc`;
    
    const response = await fetch(url);
    if (response.ok) {
      const data = await response.json();
      const items = Array.isArray(data) ? data : (data.items || []);
      if (items.length > 0) {
        const filtered = sideSlug 
          ? items.filter((it) => !it.side_slug || it.side_slug === sideSlug)
          : items;
        return (filtered.length > 0 ? filtered : items).map(normalizeFoerEfterCase).sort((a, b) => a.sortering - b.sortering);
      }
    }
  } catch (error) {
    console.warn('Kunne ikke hente fra foer_efter_cases v2 samling, forsøger fallback:', error);
  }

  // Fallback til simpelt v1 array endpoint
  try {
    const fallbackRes = await fetch(`${API_BASE_URL}/public/content/foer_efter_cases`);
    if (fallbackRes.ok) {
      const items = await fallbackRes.json();
      if (Array.isArray(items) && items.length > 0) {
        const filtered = sideSlug ? items.filter((it) => !it.side_slug || it.side_slug === sideSlug) : items;
        return (filtered.length > 0 ? filtered : items).map(normalizeFoerEfterCase).sort((a, b) => a.sortering - b.sortering);
      }
    }
  } catch (err) {
    console.error('Fejl ved hentning af foer_efter_cases fra CMS:', err);
  }

  return [];
}

/**
 * Normaliserer et projekt fra den dedikerede samling 'seneste_projekter'
 */
function normalizeSenesteProjekt(item) {
  if (!item) return null;
  const id = item.id || item.slug?._ || String(Math.random());
  const titel = item.titel || item.title || 'CRE8IT Projekt';
  const kategoriRaw = (item.kategori || item.category || '').trim();
  const undertitel = (item.undertitel || item.subtitle || '').trim();
  const beskrivelse = item.beskrivelse || item.description || '';
  const imgArr = item.billede || item.image || [];

  let images = [];
  if (Array.isArray(imgArr)) {
    images = [...new Set(imgArr.filter(Boolean))];
  } else if (typeof imgArr === 'string' && imgArr.trim()) {
    images = [imgArr.trim()];
  }
  const imageUrl = images[0] || '';
  const sortering = typeof item.sortering === 'number' ? item.sortering : 99;
  const terms = item.terms || {};

  // Bestem hovedkategori ud fra tekst eller terms
  let mainPageId = 'dekoration';
  let mainPageTitle = 'Dekoration';

  const termCat = terms['galleri-kategori']?.[0] || '';
  const katSearch = `${kategoriRaw} ${termCat}`.toLowerCase();

  if (katSearch.includes('sol')) {
    mainPageId = 'solfilm';
    mainPageTitle = 'Solfilm';
  } else if (katSearch.includes('inter')) {
    mainPageId = 'interioerfolie';
    mainPageTitle = 'Interiør folie';
  } else if (katSearch.includes('logo')) {
    mainPageId = 'logodesign';
    mainPageTitle = 'Logo design';
  } else {
    mainPageId = 'dekoration';
    mainPageTitle = 'Dekoration';
  }

  // Underkategori
  const subCategoryTitle = undertitel || mainPageTitle;
  const subCategorySlug = subCategoryTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || mainPageId;

  return {
    id,
    titel,
    title: titel,
    imageTitle: titel,
    beskrivelse,
    description: beskrivelse,
    imageDescription: beskrivelse,
    imageUrl,
    url: imageUrl,
    images,
    imageCount: images.length,
    isAlbum: images.length > 1,
    kategori: kategoriRaw || mainPageTitle,
    undertitel: subCategoryTitle,
    subCategorySlug,
    subCategoryTitle,
    categoryTitle: subCategoryTitle,
    categorySlug: subCategorySlug,
    galleryTitle: titel,
    mainPageId,
    mainPageTitle,
    badgeText: subCategoryTitle || 'Se projektbillede',
    sortering,
    terms,
  };
}

/**
 * Henter udvalgte projekter fra den dedikerede CMS-samling 'seneste_projekter'
 */
export async function fetchSenesteProjekter() {
  try {
    const response = await fetch(`${API_BASE_URL}/public/content/seneste_projekter`);
    if (response.ok) {
      const data = await response.json();
      const items = Array.isArray(data) ? data : (data.items || []);
      if (items.length > 0) {
        const normalized = items
          .map(normalizeSenesteProjekt)
          .filter((p) => p && p.imageUrl);
        normalized.sort((a, b) => a.sortering - b.sortering);
        return normalized;
      }
    }
  } catch (error) {
    console.warn('Kunne ikke hente seneste_projekter fra CMS:', error);
  }
  return [];
}


